import { PERMISSION, type KnownPermissionKey, type PermissionKey } from './permissions';

/**
 * As telas do sistema, e o que cada uma pede.
 *
 * É a fonte única de três coisas que antes discordavam entre si: o item de menu, a rota
 * que abre e o que o editor de papéis mostra. O menu escondia o Quantitativo pela
 * permissão da Estrutura, o Cronograma abria sem a leitura da Estrutura de que ele
 * depende, e a Conferência sumia para quem podia alterá-la mas não confirmar — cada um
 * decidido num lugar, e nenhum conferido contra o que a tela de fato chama.
 *
 * Cada tela tem uma permissão própria (`page.*`), e é ela que diz se a tela aparece. Ela
 * **não autoriza nada**: quem lê e grava continua sendo a permissão de dado, verificada
 * no caso de uso (RN-134). Por isso marcar a tela leva junto o que ela lê (`reads`) — uma
 * tela aberta sem a leitura do que mostra seria uma porta para "sem permissão".
 */

export type PageSection = 'Operação' | 'Configurações';

export interface PageDefinition {
  /** A permissão que abre a tela — é ela que o menu e a rota perguntam. */
  readonly permission: KnownPermissionKey;
  readonly path: string;
  readonly label: string;
  readonly section: PageSection;
  /** Para que a tela serve, em uma linha — é o que o editor de papéis mostra. */
  readonly summary: string;
  /** O que a tela lê para abrir. Marcar a tela marca estas junto. */
  readonly reads: readonly KnownPermissionKey[];
  /** O que se faz dentro dela, e que o papel pode ter ou não. */
  readonly actions: readonly KnownPermissionKey[];
  /**
   * As abas de dentro, na ordem da tela, cada uma marcada no papel (RN-626). Ausente na tela
   * de conteúdo único. Com abas, a tela só abre se alguma delas estiver marcada (RN-628).
   */
  readonly tabs?: readonly TabDefinition[];
}

/**
 * Uma aba de dentro de uma tela.
 *
 * Como a tela, ela só decide o que aparece: a aba desmarcada some da faixa, e o endereço
 * dela leva à primeira aba que o papel vê (RN-629). Não recusa leitura no servidor — o que
 * ela mostra também é lido por outras abas e telas (ADR-197).
 */
export interface TabDefinition {
  readonly permission: KnownPermissionKey;
  /** O id que a tela usa para a aba — o mesmo do `TabItem` dela. */
  readonly id: string;
  readonly label: string;
  /** O endereço da aba, quando ela tem rota própria (Quantitativo, Cronograma). */
  readonly path?: string;
}

/** A ordem é a do menu. */
export const PAGE_CATALOG: readonly PageDefinition[] = [
  {
    permission: PERMISSION.PAGE_DASHBOARD,
    path: '/',
    label: 'Painel',
    section: 'Operação',
    summary: 'Indicadores da obra e o que pede atenção.',
    reads: [PERMISSION.REPORTS_VIEW],
    actions: [],
  },
  {
    permission: PERMISSION.PAGE_PROJECTS,
    path: '/obras',
    label: 'Obras',
    section: 'Operação',
    summary: 'As obras, com prazo, endereço e situação.',
    reads: [PERMISSION.PROJECTS_VIEW],
    actions: [PERMISSION.PROJECTS_MANAGE],
  },
  {
    permission: PERMISSION.PAGE_STRUCTURE,
    path: '/estrutura',
    label: 'Estrutura',
    section: 'Operação',
    summary: 'Torres, pavimentos, disciplinas e as atividades de cada um.',
    reads: [PERMISSION.STRUCTURE_VIEW],
    actions: [PERMISSION.STRUCTURE_MANAGE, PERMISSION.SCHEDULE_EDIT, PERMISSION.MATERIALS_MANAGE],
  },
  {
    permission: PERMISSION.PAGE_TAKEOFF,
    path: '/quantitativo',
    label: 'Quantitativo',
    section: 'Operação',
    summary: 'Plantas, tipologias e os itens levantados por serviço.',
    // A árvore vem da estrutura, e a busca de insumo, do catálogo de materiais: sem uma
    // das três leituras a tela ficava presa em "Carregando…".
    reads: [PERMISSION.STRUCTURE_VIEW, PERMISSION.TAKEOFF_VIEW, PERMISSION.MATERIALS_VIEW],
    actions: [PERMISSION.TAKEOFF_MANAGE],
    tabs: [
      {
        permission: PERMISSION.TAB_TAKEOFF_ASSEMBLY,
        id: 'MONTAGEM',
        label: 'Montagem',
        path: '/quantitativo',
      },
      {
        permission: PERMISSION.TAB_TAKEOFF_ITEMS,
        id: 'ITENS',
        label: 'Itens e consolidação',
        path: '/quantitativo/itens',
      },
      {
        permission: PERMISSION.TAB_TAKEOFF_BUDGET,
        id: 'ORCAMENTO',
        label: 'Orçamento',
        path: '/quantitativo/orcamento',
      },
    ],
  },
  {
    permission: PERMISSION.PAGE_SCHEDULE,
    path: '/cronograma',
    label: 'Cronograma',
    section: 'Operação',
    summary: 'Prazos das atividades, importação e alertas de material.',
    // A grade do cronograma é a árvore da estrutura com as datas penduradas.
    reads: [PERMISSION.SCHEDULE_VIEW, PERMISSION.STRUCTURE_VIEW],
    actions: [PERMISSION.SCHEDULE_IMPORT],
    // A mão de obra primeiro (pedido de 19/10/2026): é a vista que o menu abre.
    tabs: [
      {
        permission: PERMISSION.TAB_SCHEDULE_LABOR,
        id: 'MAO_DE_OBRA',
        label: 'Mão de obra',
        path: '/cronograma/itens',
      },
      {
        permission: PERMISSION.TAB_SCHEDULE_MATERIALS,
        id: 'MATERIAIS',
        label: 'Materiais',
        path: '/cronograma',
      },
      {
        permission: PERMISSION.TAB_SCHEDULE_ROOMS,
        id: 'EXECUCAO',
        label: 'Execução por cômodo',
        path: '/cronograma/execucao',
      },
    ],
  },
  {
    permission: PERMISSION.PAGE_SUPPLIES,
    path: '/suprimentos',
    label: 'Suprimentos',
    section: 'Operação',
    summary: 'O que a obra vai precisar comprar, e as requisições enviadas.',
    reads: [PERMISSION.REQUIREMENTS_VIEW],
    actions: [PERMISSION.REQUIREMENTS_STATUS_CHANGE],
  },
  {
    permission: PERMISSION.PAGE_WORKFORCE,
    path: '/mao-de-obra',
    label: 'Mão de obra',
    section: 'Operação',
    summary: 'Funcionários, funções, equipes e serviços terceirizados.',
    reads: [PERMISSION.EMPLOYEES_VIEW],
    actions: [PERMISSION.EMPLOYEES_MANAGE],
    tabs: [
      { permission: PERMISSION.TAB_WORKFORCE_EMPLOYEES, id: 'employees', label: 'Funcionários' },
      { permission: PERMISSION.TAB_WORKFORCE_TEAMS, id: 'teams', label: 'Equipes' },
      {
        permission: PERMISSION.TAB_WORKFORCE_OUTSOURCED,
        id: 'services',
        label: 'Serviços terceirizados',
      },
      { permission: PERMISSION.TAB_WORKFORCE_ROLES, id: 'roles', label: 'Funções e disciplinas' },
    ],
  },
  {
    permission: PERMISSION.PAGE_WEEKLY_PLAN,
    path: '/planejamento',
    label: 'Planejamento',
    section: 'Operação',
    summary: 'A semana de cada pessoa, atividade por atividade.',
    reads: [PERMISSION.EMPLOYEES_VIEW],
    actions: [PERMISSION.LABOR_PLAN, PERMISSION.LABOR_ALLOCATE_OVERCAPACITY],
  },
  {
    permission: PERMISSION.PAGE_DAILY_PLAN,
    path: '/programacao',
    label: 'Programação do dia',
    section: 'Operação',
    summary: 'Quem vai onde hoje, a partir do planejamento da semana.',
    reads: [PERMISSION.EMPLOYEES_VIEW],
    actions: [PERMISSION.LABOR_PLAN],
  },
  {
    permission: PERMISSION.PAGE_INSPECTION,
    path: '/conferencia',
    label: 'Conferência',
    section: 'Operação',
    summary: 'O que foi feito no dia, medição, qualidade e o diário de obra.',
    reads: [PERMISSION.EMPLOYEES_VIEW],
    actions: [PERMISSION.LABOR_CONFIRM, PERMISSION.LABOR_MODIFY],
  },
  {
    permission: PERMISSION.PAGE_QUALITY,
    path: '/qualidade',
    label: 'Qualidade por serviço',
    section: 'Operação',
    summary: 'A ficha de verificação de cada serviço, pavimento por pavimento, e a amostragem.',
    // A ficha é a conferência vista por serviço: lê o que ela lê (`GetServiceQualitySheet`,
    // como `GetDayQuality`). Só leitura — não grava nada, e por isso não tem ação.
    reads: [PERMISSION.EMPLOYEES_VIEW],
    actions: [],
  },
  {
    permission: PERMISSION.PAGE_NON_CONFORMITIES,
    path: '/nao-conformidades',
    label: 'Não conformidades',
    section: 'Operação',
    summary: 'As não conformidades da obra: encerrar com a solução, fotos e o FORM.30.',
    // Quem abre a aba encerra (Fase 37, D8): a permissão de encerrar vem com a tela, como a
    // leitura. Separar as duas faria uma aba que mostra o botão e recusa o clique.
    reads: [PERMISSION.EMPLOYEES_VIEW, PERMISSION.NON_CONFORMITIES_MANAGE],
    actions: [],
  },
  {
    permission: PERMISSION.PAGE_DISCIPLINES,
    path: '/configuracoes/disciplinas',
    label: 'Disciplinas',
    section: 'Configurações',
    summary: 'As disciplinas e etapas que organizam a estrutura.',
    reads: [PERMISSION.STRUCTURE_VIEW],
    actions: [PERMISSION.STRUCTURE_MANAGE],
  },
  {
    permission: PERMISSION.PAGE_SERVICES,
    path: '/configuracoes/servicos',
    label: 'Serviços',
    section: 'Configurações',
    summary: 'O catálogo de serviços do quantitativo.',
    reads: [PERMISSION.TAKEOFF_VIEW],
    actions: [PERMISSION.TAKEOFF_MANAGE],
    tabs: [
      { permission: PERMISSION.TAB_SERVICES_CATALOG, id: 'catalogo', label: 'Catálogo' },
      { permission: PERMISSION.TAB_SERVICES_AVERAGES, id: 'taxas', label: 'Taxas médias' },
    ],
  },
  {
    permission: PERMISSION.PAGE_CHECKLISTS,
    path: '/configuracoes/checklists',
    label: 'Checklists',
    section: 'Configurações',
    summary: 'As fichas de verificação de cada serviço.',
    reads: [PERMISSION.STRUCTURE_VIEW],
    actions: [PERMISSION.STRUCTURE_MANAGE],
  },
  {
    permission: PERMISSION.PAGE_MATERIALS,
    path: '/configuracoes/materiais',
    label: 'Materiais',
    section: 'Configurações',
    summary: 'O catálogo de materiais e sua importação.',
    reads: [PERMISSION.MATERIALS_VIEW],
    actions: [PERMISSION.MATERIALS_MANAGE],
  },
  {
    permission: PERMISSION.PAGE_PROFILES,
    path: '/configuracoes/perfis',
    label: 'Perfis',
    section: 'Configurações',
    summary: 'Quem entra no sistema, e os papéis que definem o que cada um vê.',
    reads: [PERMISSION.USERS_MANAGE],
    actions: [PERMISSION.ROLES_MANAGE],
  },
  {
    permission: PERMISSION.PAGE_CALENDAR,
    path: '/configuracoes/calendario',
    label: 'Calendário',
    section: 'Configurações',
    summary: 'Feriados e dias sem expediente.',
    reads: [PERMISSION.EMPLOYEES_VIEW],
    actions: [PERMISSION.EMPLOYEES_MANAGE],
  },
  {
    permission: PERMISSION.PAGE_STORAGE,
    path: '/configuracoes/armazenamento',
    label: 'Armazenamento',
    section: 'Configurações',
    // O backup, o expurgo das importações e o arquivamento das fotos saíram com o banco do
    // navegador (Fase 32); a página ficou com a cota do aparelho.
    summary: 'O espaço que o sistema ocupa neste navegador, e o pedido para ele não apagar.',
    reads: [PERMISSION.BACKUP_MANAGE],
    actions: [],
  },
  {
    permission: PERMISSION.PAGE_INTEGRITY,
    path: '/configuracoes/integridade',
    label: 'Integridade',
    section: 'Configurações',
    summary: 'Verifica e corrige inconsistências nos dados.',
    reads: [PERMISSION.SYSTEM_INTEGRITY],
    actions: [],
  },
  {
    permission: PERMISSION.PAGE_AUDIT,
    path: '/configuracoes/auditoria',
    label: 'Auditoria',
    section: 'Configurações',
    summary: 'Quem alterou o quê, e quando.',
    reads: [PERMISSION.AUDIT_VIEW],
    actions: [],
  },
];

/**
 * O que cada ação pressupõe.
 *
 * Quem edita a estrutura precisa enxergá-la, e quem aloca acima da capacidade está
 * planejando. Um papel com a ação e sem a leitura teria um botão numa tela que não abre.
 */
export const PERMISSION_IMPLIES: Readonly<Partial<Record<KnownPermissionKey, KnownPermissionKey>>> =
  {
    [PERMISSION.PROJECTS_MANAGE]: PERMISSION.PROJECTS_VIEW,
    [PERMISSION.STRUCTURE_MANAGE]: PERMISSION.STRUCTURE_VIEW,
    [PERMISSION.TAKEOFF_MANAGE]: PERMISSION.TAKEOFF_VIEW,
    [PERMISSION.SCHEDULE_IMPORT]: PERMISSION.SCHEDULE_VIEW,
    [PERMISSION.SCHEDULE_EDIT]: PERMISSION.SCHEDULE_VIEW,
    [PERMISSION.MATERIALS_MANAGE]: PERMISSION.MATERIALS_VIEW,
    [PERMISSION.REQUIREMENTS_STATUS_CHANGE]: PERMISSION.REQUIREMENTS_VIEW,
    [PERMISSION.RESTRICTIONS_MANAGE]: PERMISSION.RESTRICTIONS_VIEW,
    [PERMISSION.EMPLOYEES_MANAGE]: PERMISSION.EMPLOYEES_VIEW,
    [PERMISSION.LABOR_PLAN]: PERMISSION.EMPLOYEES_VIEW,
    [PERMISSION.LABOR_CONFIRM]: PERMISSION.EMPLOYEES_VIEW,
    [PERMISSION.LABOR_MODIFY]: PERMISSION.EMPLOYEES_VIEW,
    [PERMISSION.LABOR_ALLOCATE_OVERCAPACITY]: PERMISSION.LABOR_PLAN,
  };

/**
 * O que uma permissão traz junto: as leituras da tela, a tela da aba, ou o que a ação
 * pressupõe.
 *
 * A aba traz a tela, e a tela não traz as abas (RN-627): se trouxesse, nenhuma aba se
 * desmarcaria enquanto a tela estivesse marcada. Quem marca todas ao marcar a tela é o
 * editor de papéis.
 */
export function requiredBy(permission: KnownPermissionKey): readonly KnownPermissionKey[] {
  const page = PAGE_CATALOG.find((candidate) => candidate.permission === permission);
  if (page !== undefined) return page.reads;
  const owner = pageOfTab(permission);
  if (owner !== undefined) return [owner.permission];
  const implied = PERMISSION_IMPLIES[permission];
  return implied === undefined ? [] : [implied];
}

/** A tela dona de uma permissão de aba. */
export function pageOfTab(permission: PermissionKey): PageDefinition | undefined {
  return PAGE_CATALOG.find((page) => page.tabs?.some((tab) => tab.permission === permission));
}

/** As abas da tela que um conjunto de permissões mostra, na ordem da tela. */
export function tabsOpenedBy(
  page: PageDefinition,
  permissions: ReadonlySet<PermissionKey>,
): TabDefinition[] {
  return (page.tabs ?? []).filter((tab) => permissions.has(tab.permission));
}

/**
 * O conjunto fechado: as permissões pedidas e tudo o que elas trazem, até não sobrar
 * nada a acrescentar. É a mesma conta na tela e no caso de uso — a tela mostra travado o
 * que o servidor acrescentaria de qualquer jeito.
 */
export function completePermissions(
  permissions: Iterable<KnownPermissionKey>,
): Set<KnownPermissionKey> {
  const complete = new Set<KnownPermissionKey>();
  const pending = [...permissions];
  while (pending.length > 0) {
    const next = pending.pop()!;
    if (complete.has(next)) continue;
    complete.add(next);
    pending.push(...requiredBy(next));
  }
  return complete;
}

/**
 * Por que esta permissão não pode sair: as permissões marcadas que a trazem junto.
 *
 * Vazio = ela é escolha livre. É o que a tela usa para travar a caixa e dizer de quem é a
 * culpa ("a tela Planejamento lê isto").
 */
export function heldBy(
  permission: KnownPermissionKey,
  selected: ReadonlySet<KnownPermissionKey>,
): KnownPermissionKey[] {
  return [...selected].filter(
    (other) => other !== permission && completePermissions([other]).has(permission),
  );
}

/**
 * A tela com abas pedida sem nenhuma delas leva todas (RN-628).
 *
 * É o que um papel gravado antes das abas pedia — a tela inteira —, e é como a API continua
 * entendendo quem não fala de aba. O editor nunca manda uma tela sem aba: desmarcar a última
 * desmarca a tela.
 */
export function withTabsOfBarePages(
  permissions: ReadonlySet<KnownPermissionKey>,
): Set<KnownPermissionKey> {
  const complete = new Set(permissions);
  for (const page of PAGE_CATALOG) {
    if (page.tabs === undefined || !complete.has(page.permission)) continue;
    if (tabsOpenedBy(page, complete).length > 0) continue;
    for (const tab of page.tabs) complete.add(tab.permission);
  }
  return complete;
}

/**
 * As telas que um conjunto de permissões abre, na ordem do menu.
 *
 * A tela com abas pede alguma delas (RN-628): marcada sem nenhuma, ela abriria vazia, sem
 * faixa e sem conteúdo. Gravado pelo caso de uso, o papel nunca chega assim
 * (`withTabsOfBarePages`); a conta fica aqui para o conjunto que vem de outro lugar.
 */
export function pagesOpenedBy(permissions: ReadonlySet<PermissionKey>): PageDefinition[] {
  return PAGE_CATALOG.filter(
    (page) =>
      permissions.has(page.permission) &&
      (page.tabs === undefined || tabsOpenedBy(page, permissions).length > 0),
  );
}

/**
 * A aba dona do caminho, na tela dona dele.
 *
 * A mais comprida que casa: no Cronograma, `/cronograma` é a aba Materiais e também o
 * começo do endereço das outras duas. Na tela de abas sem rota (Mão de obra, Serviços), não
 * há aba no caminho, e a tela decide pela escolha dela.
 */
export function tabForPath(pathname: string): TabDefinition | undefined {
  const page = pageForPath(pathname);
  const candidates = (page?.tabs ?? []).filter(
    (tab) =>
      tab.path !== undefined && (pathname === tab.path || pathname.startsWith(`${tab.path}/`)),
  );
  return candidates.sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0];
}

/**
 * A tela dona do caminho — a rota pergunta por aqui.
 *
 * Os subcaminhos são da mesma tela (`/quantitativo/itens` é o Quantitativo); a raiz não
 * tem subcaminho, senão todo endereço seria do Painel.
 */
export function pageForPath(pathname: string): PageDefinition | undefined {
  return PAGE_CATALOG.find((page) =>
    page.path === '/'
      ? pathname === '/'
      : pathname === page.path || pathname.startsWith(`${page.path}/`),
  );
}
